import React from 'react'
import styled, { css } from 'styled-components'
import { Dialog, Block, H5, P, Button } from '@mote/ui'
import {
  TickFilledIcon,
  PlusIcon,
  LogoNoText,
  ChromeIcon,
  AppleIcon
} from '@mote/assets/icons'
import { useMediaQuery } from '@mote/helpers'
import { useAccountQuery, useTrackMutation } from '@mote/api'
import { reallyTruthy } from '@mote/helpers/validation'
import {
  CWS_URL,
  EXTENSION_INSTALLED_STORAGE_KEY,
  IOS_STORE_URL
} from '@mote/helpers/constants'
import { useGoalsOnboarding, useConfetti } from '@mote/helpers/hooks/business'
import { getGodModeOptions } from 'web/src/shared/Utils'
import { getMoteLogin, identifyMaybeAndBroadcast } from 'web/src/shared/Auth'
import { useAuth } from '../../auth/useAuth'
import { Content } from './components'
import { isAndroid, isIOS } from '@mote/helpers/browser'
import Confetti from 'react-canvas-confetti'
import { snakify } from '@mote/helpers/misc'

const MAX_POLLS = 60
const POLL_SLEEP = 2000

const confettiStyles = {
  position: 'fixed',
  pointerEvents: 'none',
  width: '100%',
  height: '100%',
  zIndex: 100,
  top: 0,
  left: 0
}

export const OnboardingProduct = () => {
  const cancelPolling = React.useRef(null)
  const goalsState = useGoalsOnboarding()
  const { confettiRef, fireConfetti } = useConfetti()
  const { updateUser } = useAuth()
  const { mutate: queryAccount } = useAccountQuery()
  const { mutate: track, loading: isTrackMutating } = useTrackMutation()
  const [onboardingComplete, setOnboardingComplete] = React.useState(
    !goalsState.makeFirstMoteRequired && goalsState.productOnboardingRequired
  )
  const [isGotoHomepageLoading, setIsGotoHomepageLoading] =
    React.useState(false)
  const mediaBreakPoint = useMediaQuery([640, 920, 1200, 1600])
  const isMobile = React.useMemo(
    () => mediaBreakPoint <= 640,
    [mediaBreakPoint]
  )
  const tasks = React.useMemo(() => {
    const extensionInstalled = reallyTruthy(
      window.localStorage.getItem(EXTENSION_INSTALLED_STORAGE_KEY)
    )

    let chromeExtensionTask = {
      key: 'install-extension',
      icon: ChromeIcon,
      title: 'Install the Chrome Extension',
      subtitle: 'Available on the Chrome Web Store',
      ctaText: 'Install the extension',
      completed: extensionInstalled
    }

    if (isIOS()) {
      chromeExtensionTask = {
        key: 'install-app',
        icon: AppleIcon,
        title: 'Download the iOS app',
        subtitle: 'The power of mote, in your pocket',
        ctaText: 'Download the app',
        completed: false
      }
    } else if (isAndroid) {
      chromeExtensionTask = {
        ...chromeExtensionTask,
        ctaText: 'Install on desktop',
        completed: true
      }
    }

    return [
      {
        key: 'create-account',
        icon: PlusIcon,
        title: 'Create a Mote account',
        subtitle: 'It’s simple and takes 2 minutes',
        completed: true
      },
      chromeExtensionTask,
      {
        key: 'create-mote',
        icon: LogoNoText,
        title: 'Create your first Mote',
        subtitle: 'It’s as simple as 1, 2, 3!',
        ctaText: 'Leave your first mote',
        completed: !goalsState.makeFirstMoteRequired
      }
    ]
  }, [goalsState])

  React.useEffect(() => {
    if (!goalsState.makeFirstMoteRequired) return

    pollAccountStatus()

    return () => {
      clearTimeout(cancelPolling.current)
    }
  }, [])

  React.useEffect(() => {
    if (
      tasks.filter(({ completed }) => !!completed).length !== tasks.length ||
      goalsState.onboardingComplete
    )
      return

    setOnboardingComplete(true)
  }, [tasks])

  React.useEffect(() => {
    if (!onboardingComplete) return

    fireConfetti()
    fireConfetti(0, { y: 0, x: 0 })
    fireConfetti(-180, { y: 0, x: 1 })

    return () => confettiRef.current?.reset()
  }, [onboardingComplete])

  async function handleGoToHomepage() {
    setIsGotoHomepageLoading(true)
    await track({
      variables: snakify({
        event: 'Goal Completed',
        goalName: 'productOnboarding'
      })
    })

    updateUser(getMoteLogin())
  }

  async function pollAccountStatus(polls = 0) {
    const response = await queryAccount({
      variables: getGodModeOptions() || {}
    })

    if (!response || response.error || !response.data) {
      if (isGotoHomepageLoading) setIsGotoHomepageLoading(false)
      return
    }

    const { goals } = response.data

    if (goals?.makeFirstMote !== 'completed') {
      if (polls < MAX_POLLS) {
        cancelPolling.current = setTimeout(
          () => pollAccountStatus(polls + 1),
          POLL_SLEEP
        )
      }
    } else {
      identifyMaybeAndBroadcast()
      updateUser(prev => ({
        ...prev,
        goals: {
          ...prev.goals,
          makeFirstMote: goals.makeFirstMote
        }
      }))
    }
  }

  function handleAction(key) {
    switch (key) {
      case 'install-extension': {
        track({
          variables: {
            event: 'Install Extension Clicked',
            referrer_user_id: window.localStorage.getItem(
              'mote_referrer_user_id'
            )
          }
        })
        window.open(CWS_URL, '_new')
        break
      }
      case 'install-app': {
        window.open(IOS_STORE_URL, '_new')
        break
      }
      case 'create-mote': {
        window.open(
          'https://docs.google.com/document/d/1K4QuYWjSaoH2cU5o28lsAD1uinNVNq-fe9Jj72M1Kwg/copy',
          '_new'
        )
        break
      }
      default:
    }
  }

  return (
    <>
      <Content
        title='Getting Started'
        subtitle='Follow these simple steps to complete your account setup'
        rows={3}
        columns={1}
      >
        {tasks.map(
          ({ key, icon: Icon, title, subtitle, completed, ctaText }) => {
            const iconColor = key === 'create-account' ? '#25252D' : null
            return (
              <TaskContainer
                key={key}
                layout='horizontal'
                align='center'
                justify='space-between'
                gap={16}
                disabled={completed}
                onClick={
                  !completed && isMobile ? () => handleAction(key) : null
                }
              >
                <TaskDetailsContainer
                  layout='horizontal'
                  align='center'
                  justify='space-between'
                  gap={16}
                  disabled={completed}
                >
                  <TaskImage className='task-image'>
                    {React.createElement(Icon, {
                      color: iconColor,
                      width: 24,
                      height: 24
                    })}
                  </TaskImage>
                  <Block layout='vertical' gap={8}>
                    <H5
                      fontSize={15}
                      fontWeight={500}
                      lineHeight='100%'
                      color='#25252D'
                    >
                      {title}
                    </H5>
                    <P
                      fontSize={15}
                      fontWeight={400}
                      lineHeight='100%'
                      color='#6C6C84'
                    >
                      {subtitle}
                    </P>
                  </Block>
                </TaskDetailsContainer>
                {!completed && !isMobile && (
                  <Button text={ctaText} onClick={() => handleAction(key)} />
                )}
                {completed && (
                  <TaskCompletedIcon>
                    <TickFilledIcon color={{ background: '#57C96A' }} />
                  </TaskCompletedIcon>
                )}
              </TaskContainer>
            )
          }
        )}
      </Content>
      {onboardingComplete && (
        <>
          <Dialog
            isOpen
            width={320}
            canClose={false}
            canEscapeKeyClose={false}
            canOutsideClickClose={false}
            style={{
              backdrop: {
                opacity: 1,
                background: 'rgba(113, 101, 129, 0.25)'
              },
              card: {
                padding: '24px 32px',
                boxShadow:
                  '0px 1px 3px rgba(0, 0, 0, 0.04), 0px 3px 14px rgba(0, 0, 0, 0.08), 0px 8px 32px rgba(0, 0, 0, 0.1)',
                borderRadius: 8
              }
            }}
          >
            <Dialog.Heading
              style={{
                border: 'none',
                padding: 0,
                fontSize: 17,
                lineHeight: '100%',
                fontWeight: 500,
                color: '#25252D',
                height: 'auto'
              }}
            >
              What a pro! 🎉
            </Dialog.Heading>
            <Dialog.Content style={{ padding: 0 }}>
              <P
                fontSize={15}
                lineHeight='150%'
                fontWeight={400}
                color='#6C6C84'
                alignment='left'
              >
                You’ve completed onboarding. You’ll now be taken back to your
                account page. Happy moting!
              </P>
            </Dialog.Content>
            <Dialog.ButtonGroup
              alignment='center'
              style={{ padding: 0, margin: '16px 0 -16px' }}
            >
              <GoHomeButton
                onClick={handleGoToHomepage}
                text='Close'
                loadingText='Processing'
                variant='soft'
                loading={isGotoHomepageLoading || isTrackMutating}
                block
              />
            </Dialog.ButtonGroup>
          </Dialog>
          <Confetti
            refConfetti={ref => (confettiRef.current = ref)}
            style={confettiStyles}
          />
        </>
      )}
    </>
  )
}

const TaskContainer = styled(Block)`
  min-height: 100px;
  padding: 24px;
  border-radius: 8px;
  margin: 0 -24px;
  transition: all 150ms ease-in;

  ${({ disabled }) =>
    disabled
      ? css`
          pointer-events: none;

          button {
            pointer-events: none;
            color: rgba(37, 37, 45, 0.3);
            background: #ececf3;
          }
        `
      : css`
          cursor: pointer;
        `}

  &:hover {
    background: #f6f6f9;

    .task-image {
      background: #ececf3;
    }
  }

  @media (max-width: 639px) {
    padding: 16px;
    margin: 0 -16px;
  }
`

const TaskDetailsContainer = styled(Block)`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: opacity 150ms ease-in;
`

const TaskImage = styled(Block)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 32px;
  width: 88px;
  min-height: 56px;
  background: #f6f6f9;
  border-radius: 4px;
  transition: all 150ms ease-in;
`

const TaskCompletedIcon = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 639px) {
    width: 28px;
    height: 28px;
  }
`

const GoHomeButton = styled(Button)``
